/**
 * useGeometryWholeDrag Hook
 *
 * Generic hook for dragging entire geometries (lines, shapes, future types).
 * Uses polymorphic configuration pattern to eliminate code duplication.
 *
 * Benefits:
 * - Single source of truth for drag logic
 * - Type-safe with TypeScript generics
 * - Easy to extend for new geometry types (~30 lines of config)
 * - Eliminates 95% code duplication between useLineDrag and useShapeDrag
 * - Supports both mouse and touch events (mobile drag)
 *
 * @module hooks/drag/useGeometryWholeDrag
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import type { MapMouseEvent, MapTouchEvent } from '@vis.gl/react-maplibre';
import type * as GeoJSON from 'geojson';
import maplibregl from 'maplibre-gl';
import { useGeoMarkupStore } from '../../store';
import { useDrawingTools } from '../useDrawingTools';
import { GeometryType } from '../../types/geometry';
import type { Coordinates, TaskGeometry } from '../../types';
import { useGeometryAutoPan } from '../useGeometryAutoPan';
import {
  MOUSE_BUTTON_RIGHT,
  CURSOR_GRABBING,
  CURSOR_DEFAULT,
  PREVIEW_TASK_ID,
} from '../../constants/geometry';

/**
 * Configuration for geometry-specific drag behavior
 * Defines how different geometry types should be handled during drag
 */
export interface GeometryDragConfig {
  /** Prefix for MapLibre source ID (e.g., 'line-source-', 'shape-source-') */
  sourcePrefix: string;

  /** Prefix for interactive layer ID (e.g., 'line-hitbox-', 'shape-hitbox-') */
  layerPrefix: string;

  /** CSS selector for handles to skip (e.g., '[data-point-index]') */
  handleSelector: string;

  /** Extract points array from geometry */
  getPoints: (geometry: TaskGeometry) => Coordinates[] | undefined;

  /** Create updated geometry with new points */
  createUpdatedGeometry: (
    originalGeometry: TaskGeometry,
    newPoints: Coordinates[]
  ) => Partial<TaskGeometry>;

  /** Calculate new center for geometry after drag */
  calculateCenter: (
    geometry: TaskGeometry,
    newPoints: Coordinates[],
    originalCenter: Coordinates | null,
    offset: { lat: number; lng: number }
  ) => Coordinates;

  /** Create GeoJSON for MapLibre preview during drag */
  createGeoJson: (geometry: TaskGeometry) => GeoJSON.GeoJSON;

  /** Whether this geometry type needs to track original center */
  needsOriginalCenter: boolean;
}

/**
 * Generic hook for whole geometry drag functionality
 *
 * Provides drag support for entire geometries (not individual vertices).
 * Handles mouse events, auto-pan, MapLibre updates, and Zustand state management.
 *
 * @param geometryId - The ID of the geometry being dragged
 * @param taskId - The ID of the task this geometry belongs to
 * @param config - Type-specific drag configuration
 * @returns Object containing drag state
 */
export const useGeometryWholeDrag = (
  geometryId: string,
  taskId: string,
  config: GeometryDragConfig
) => {
  const { current: map } = useMap();
  const { updateGeometry, currentGeometries } = useDrawingTools();

  // Conditions for drag enablement (Zustand selectors)
  const isTaskFormOpen = useGeoMarkupStore((s) => s.tasks.formIsOpen);
  const isDrawing = useGeoMarkupStore((s) => s.isDrawing);
  const selectedTaskId = useGeoMarkupStore((s) => s.tasks.selectedId);
  const activeGeometryId = useGeoMarkupStore((s) => s.activeGeometryId);
  const activeTool = useGeoMarkupStore((s) => s.activeTool);

  // Zustand drag state (enables auto-pan)
  const setDraggingGeometry = useGeoMarkupStore((s) => s.setDraggingGeometry);
  const setWholeDragActive = useGeoMarkupStore((s) => s.setWholeDragActive);
  const isDragging = useGeoMarkupStore(
    (s) => s.draggingGeometryId === geometryId
  );

  // Find geometry from current geometries
  const geometry = currentGeometries.find((g) => g.id === geometryId);

  // Auto-pan support for smooth dragging experience
  const { smartPan, initializeVelocityTracking, stopAutoPan } =
    useGeometryAutoPan(geometryId);

  // Calculate isDraggable based on context (preview vs existing task)
  const isDraggable =
    taskId === PREVIEW_TASK_ID
      ? isDrawing &&
        isTaskFormOpen &&
        activeGeometryId === geometryId &&
        activeTool !== GeometryType.Selection
      : isDrawing &&
        isTaskFormOpen &&
        selectedTaskId === taskId &&
        activeTool !== GeometryType.Selection;

  // Drag calculation refs (not React state - just calculations)
  const startLngLatRef = useRef<{ lat: number; lng: number } | null>(null);
  const originalPointsRef = useRef<Coordinates[]>([]);
  const originalCenterRef = useRef<Coordinates | null>(null);

  // MapLibre layer and source IDs
  const interactiveLayerId = `${config.layerPrefix}${geometryId}`;
  const sourceId = `${config.sourcePrefix}${geometryId}`;

  // =========================================================================
  // SHARED DRAG LOGIC (DRY - used by both mouse and touch handlers)
  // =========================================================================

  /**
   * Start drag operation (shared by mouse and touch handlers)
   * @returns true if drag started, false if not on geometry
   */
  const startDrag = useCallback(
    (lngLat: { lat: number; lng: number }, target: HTMLElement): boolean => {
      if (!map || !geometry) return false;

      // Skip if clicking on handles (point/resize handles)
      const isHandle = target.closest(config.handleSelector);
      if (isHandle) return false;

      // Guard: Check if layer exists before querying
      if (!map.getLayer(interactiveLayerId)) return false;

      // Project lngLat to screen point for queryRenderedFeatures
      const screenPoint = map.project([lngLat.lng, lngLat.lat]);

      // Query features at click/touch point
      const features = map.queryRenderedFeatures(screenPoint, {
        layers: [interactiveLayerId],
      });

      if (features.length === 0) return false;

      // Start drag - store initial lngLat position
      // Set explicit whole-drag flag FIRST to prevent race condition with PointMarkers
      setWholeDragActive(true);
      setDraggingGeometry(geometryId);
      startLngLatRef.current = { lat: lngLat.lat, lng: lngLat.lng };

      // Store original points for offset calculation
      const points = config.getPoints(geometry);
      originalPointsRef.current = points ? [...points] : [];

      // Store original center if needed (shape vs line difference)
      if (config.needsOriginalCenter) {
        originalCenterRef.current = { ...geometry.calculatedCenter };
      }

      // Initialize velocity tracking for auto-pan
      initializeVelocityTracking(lngLat);

      // Change cursor to grabbing
      map.getCanvas().style.cursor = CURSOR_GRABBING;

      return true;
    },
    [
      map,
      geometry,
      geometryId,
      interactiveLayerId,
      config,
      setWholeDragActive,
      setDraggingGeometry,
      initializeVelocityTracking,
    ]
  );

  /**
   * Update drag position (shared by mouse and touch handlers)
   * Updates geometry preview at 60fps without React re-renders
   */
  const updateDrag = useCallback(
    (lngLat: { lat: number; lng: number }) => {
      if (!isDragging || !map || !geometry || !startLngLatRef.current) return;

      // Calculate offset from start position
      const offset = {
        lat: lngLat.lat - startLngLatRef.current.lat,
        lng: lngLat.lng - startLngLatRef.current.lng,
      };

      // Calculate new points with offset
      const newPoints = originalPointsRef.current.map((point) => ({
        lat: point.lat + offset.lat,
        lng: point.lng + offset.lng,
      }));

      // Auto-pan map when dragging near edges (works for both mouse and touch)
      smartPan(lngLat);

      // Update MapLibre source directly (60fps without React re-renders)
      const source = map.getSource(sourceId) as maplibregl.GeoJSONSource;
      if (source && source.type === 'geojson') {
        const previewGeometry = {
          ...geometry,
          ...config.createUpdatedGeometry(geometry, newPoints),
        };
        const geoJson = config.createGeoJson(previewGeometry);
        source.setData(geoJson);
      }
    },
    [isDragging, map, geometry, sourceId, config, smartPan]
  );

  /**
   * End drag operation (shared by mouse and touch handlers)
   * Finalizes drag and updates Zustand (triggers history)
   */
  const endDrag = useCallback(
    (lngLat: { lat: number; lng: number }) => {
      if (!isDragging || !map || !geometry || !startLngLatRef.current) return;

      // Calculate final offset from start position
      const offset = {
        lat: lngLat.lat - startLngLatRef.current.lat,
        lng: lngLat.lng - startLngLatRef.current.lng,
      };

      // Calculate final points
      const newPoints = originalPointsRef.current.map((point) => ({
        lat: point.lat + offset.lat,
        lng: point.lng + offset.lng,
      }));

      // Calculate final center (different for line vs shape)
      const newCenter = config.calculateCenter(
        geometry,
        newPoints,
        originalCenterRef.current,
        offset
      );

      // Single Zustand update with final position (triggers history)
      updateGeometry(geometryId, {
        ...config.createUpdatedGeometry(geometry, newPoints),
        calculatedCenter: newCenter,
      });

      // Stop auto-panning
      stopAutoPan();

      // Clear dragging state in Zustand
      // Clear whole-drag flag FIRST, then geometry (reverse of start order)
      setWholeDragActive(false);
      setDraggingGeometry(null);

      // Reset calculation refs
      startLngLatRef.current = null;
      originalPointsRef.current = [];
      originalCenterRef.current = null;

      // Reset cursor to default
      map.getCanvas().style.cursor = CURSOR_DEFAULT;
    },
    [
      isDragging,
      map,
      geometry,
      geometryId,
      config,
      updateGeometry,
      stopAutoPan,
      setWholeDragActive,
      setDraggingGeometry,
    ]
  );

  // =========================================================================
  // EVENT REGISTRATION (mouse + touch)
  // =========================================================================

  /**
   * Setup global mouse and touch events for drag
   * Both event types use shared startDrag/updateDrag/endDrag methods (DRY)
   */
  useEffect(() => {
    if (!map || !isDraggable || !geometry) return;

    // ===== MOUSE EVENT HANDLERS (thin wrappers) =====

    const handleMouseDown = (e: MapMouseEvent) => {
      // Skip right-click (allow context menu)
      if (e.originalEvent.button === MOUSE_BUTTON_RIGHT) return;

      const target = e.originalEvent.target as HTMLElement;
      if (startDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng }, target)) {
        e.preventDefault();
        e.originalEvent.stopPropagation();
      }
    };

    const handleMouseMove = (e: MapMouseEvent) => {
      updateDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };

    const handleMouseUp = (e: MapMouseEvent) => {
      endDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };

    // ===== TOUCH EVENT HANDLERS (thin wrappers) =====

    const handleTouchStart = (e: MapTouchEvent) => {
      // Single touch only (ignore multi-touch gestures like pinch-zoom)
      if (e.originalEvent.touches.length !== 1) return;

      const target = e.originalEvent.target as HTMLElement;
      if (startDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng }, target)) {
        e.preventDefault();
        e.originalEvent.stopPropagation();
      }
    };

    const handleTouchMove = (e: MapTouchEvent) => {
      // Single touch only
      if (e.originalEvent.touches.length !== 1) return;
      // Prevent MapLibre's built-in map drag during geometry drag
      if (startLngLatRef.current) {
        e.preventDefault();
      }
      updateDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };

    const handleTouchEnd = (e: MapTouchEvent) => {
      // Use changedTouches for touchend (touches array is empty)
      if (e.originalEvent.changedTouches.length === 0) return;
      endDrag({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };

    // Register mouse events
    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    // Register touch events (mobile drag support)
    map.on('touchstart', handleTouchStart);
    map.on('touchmove', handleTouchMove);
    map.on('touchend', handleTouchEnd);

    // Cleanup: Remove all event listeners
    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      map.off('touchstart', handleTouchStart);
      map.off('touchmove', handleTouchMove);
      map.off('touchend', handleTouchEnd);
    };
  }, [map, isDraggable, geometry, startDrag, updateDrag, endDrag]);

  return {
    isDraggable,
  };
};
